import { useTranslation } from "react-i18next";

export interface ProductDetailLegendProps {
  /** `product.legend` — el aviso legal de la etiqueta. */
  legend: string | undefined;
}

/**
 * El aviso legal de la ficha — el "BLOQUE 14b" del original.
 *
 * ── Por qué es un bloque y no una nota al pie ────────────────────────
 * Es lo que la COFEPRIS exige que aparezca junto a un suplemento: "este
 * producto no es un medicamento", la advertencia de dosis, el aviso de
 * embarazo. No es letra pequeña decorativa — es la razón de que `legend`
 * admita dos mil caracteres en el contrato — y la ficha no lo pintaba en
 * ningún sitio, así que el único producto sembrado que lo trae lo tenía
 * guardado y sin enseñar.
 *
 * ── Medidas del original ─────────────────────────────────────────────
 * Caja de `surface` con borde `line` y radio 14 px (`--r-md`, el único
 * bloque de la ficha que no usa el radio 3 de todo lo demás), padding
 * 20/24, rótulo con 10 px de aire debajo y el texto a 12 px con
 * interlínea 1.65.
 *
 * ── `white-space: pre-wrap` no es un detalle ─────────────────────────
 * El aviso viene con sus saltos de línea escritos por quien administra el
 * catálogo, y son los que separan una advertencia de la siguiente. Sin
 * esto se leería como un solo párrafo y "no se deje al alcance de los
 * niños" quedaría pegado a la frase anterior.
 *
 * ── El color sube a `ink-3` ──────────────────────────────────────────
 * El original lo pinta en `ink-4` (#8A8377), que sobre blanco da 3.48:1.
 * Un aviso legal a 12 px que no llega a 4.5:1 es exactamente el texto que
 * no puede quedarse por debajo del mínimo.
 */
export function ProductDetailLegend({ legend }: ProductDetailLegendProps) {
  const { t } = useTranslation();

  if (!legend?.trim()) return null;

  return (
    <section className="rounded-ds-md border border-ds-border bg-white px-6 py-5">
      <h2 className="mb-2.5 font-mono text-[11px] uppercase leading-none tracking-[0.2em] text-ds-ink-3">
        {t("Leyenda / Advertencias")}
      </h2>
      <p className="whitespace-pre-wrap text-[12px] leading-[1.65] text-ds-ink-3">
        {legend}
      </p>
    </section>
  );
}
